import { Fragment } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { CloseIcon } from "../../icons";
import { CRYPTO_MODAL_PANEL_HEIGHT_CLASS, CRYPTO_MODAL_WRAPPER_CLASS } from "../modalLayout";

const CryptoModal = ({
  isOpen,
  onClose,
  headerTabs,
  children,
  maxWidth = CRYPTO_MODAL_WRAPPER_CLASS,
  wrapperClassName = "",
  panelClassName = "",
  headerClassName = "",
  guideHint = null,
  mobileBottomSheet = false,
  mobileFullScreen = false,
  mobileSpotlightActive = false,
  isLoading = false,
  showCancel = true,
  showBack = true,
  headerLeft = null,
  contentSizedOnDesktop = false,
  closeOnOutsideClick = true,
}) => {
  const showMobileBackdrop =
    mobileFullScreen && (mobileSpotlightActive || Boolean(guideHint));
  const skipMobileExternalHint = guideHint?.inlineOnMobile;
  const panelHeightClass = contentSizedOnDesktop
    ? "sm:h-auto sm:max-h-none sm:min-h-0"
    : CRYPTO_MODAL_PANEL_HEIGHT_CLASS;

  return (
    <Transition.Root show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={closeOnOutsideClick ? onClose : () => {}}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div
            className={`fixed inset-0 ${
              mobileFullScreen
                ? showMobileBackdrop
                  ? "bg-black/60 backdrop-blur-[3px] sm:bg-black/60 sm:backdrop-blur-[3px]"
                  : "bg-transparent sm:bg-black/60 sm:backdrop-blur-[3px]"
                : "bg-black/60 backdrop-blur-[3px]"
            }`}
          />
        </Transition.Child>

        <div
          className={`fixed inset-0 z-50 ${
            mobileSpotlightActive
              ? "max-sm:overflow-visible sm:overflow-y-auto sm:overflow-x-hidden"
              : "overflow-y-auto overflow-x-hidden"
          } ${mobileBottomSheet || mobileFullScreen ? "p-0 sm:p-5" : "p-4 sm:p-5"}`}
        >
          <div
            className={`flex min-h-full sm:h-full justify-center ${
              mobileBottomSheet
                ? "items-end sm:items-center"
                : mobileFullScreen
                  ? "items-stretch sm:items-center"
                  : "items-center"
            }`}
          >
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom={
                mobileBottomSheet
                  ? "opacity-0 translate-y-full sm:translate-y-4 sm:scale-95"
                  : mobileFullScreen
                    ? "opacity-0 translate-x-4 sm:translate-x-0 sm:translate-y-4 sm:scale-95"
                    : "opacity-0 translate-y-4 sm:scale-95"
              }
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo={
                mobileBottomSheet
                  ? "opacity-0 translate-y-full sm:translate-y-4 sm:scale-95"
                  : mobileFullScreen
                    ? "opacity-0 translate-x-4 sm:translate-x-0 sm:translate-y-4 sm:scale-95"
                    : "opacity-0 translate-y-4 sm:scale-95"
              }
            >
              <Dialog.Panel
                className={`relative w-full bg-transparent p-0 shadow-none ${
                  wrapperClassName || maxWidth
                }`}
              >
                {guideHint?.position === "left" && (
                  <>
                    <div className="pointer-events-none absolute inset-y-0 right-full z-10 hidden w-[300px] items-center pr-6 lg:flex">
                      <div className="pointer-events-auto w-full">{guideHint.content}</div>
                    </div>
                    {!skipMobileExternalHint && (
                      <div className="pointer-events-auto mb-4 w-full max-w-[300px] lg:hidden">
                        {guideHint.content}
                      </div>
                    )}
                  </>
                )}

                <div
                  className={
                    mobileBottomSheet
                      ? "flex w-full flex-col items-center sm:block"
                      : undefined
                  }
                >
                  {mobileBottomSheet && (
                    <button
                      type="button"
                      onClick={onClose}
                      className="mb-4 flex size-10 shrink-0 items-center justify-center rounded-full bg-[#9CA3AF] shadow-[0_2px_8px_rgba(0,0,0,0.15)] custom-transition hover:bg-[#8B939E] sm:hidden"
                      aria-label="Close"
                    >
                      <CloseIcon className="text-white" />
                    </button>
                  )}

                  <div
                    className={`relative flex w-full flex-col bg-background-surface shadow-[0_4px_24px_rgba(0,0,0,0.16)] ${
                      mobileSpotlightActive
                        ? "overflow-hidden max-sm:overflow-visible"
                        : "overflow-hidden"
                    } ${
                      mobileBottomSheet
                        ? `gap-10 rounded-t-[24px] rounded-b-none py-6 sm:rounded-[16px] ${panelHeightClass}`
                        : mobileFullScreen
                          ? mobileSpotlightActive
                            ? `h-dvh max-h-dvh gap-0 rounded-none bg-transparent p-0 sm:gap-10 sm:rounded-[16px] sm:bg-background-surface sm:py-6 ${panelHeightClass}`
                            : `h-dvh max-h-dvh gap-4 rounded-none sm:gap-10 sm:rounded-[16px] sm:py-6 ${panelHeightClass}`
                          : `gap-10 rounded-[16px] py-6 ${panelHeightClass}`
                    } ${panelClassName}`}
                  >
                    {mobileSpotlightActive && (
                      <div
                        className="absolute inset-0 z-30 bg-black/50 sm:hidden"
                        aria-hidden
                      />
                    )}

                    
                    <div
                      className={`relative flex flex-col sm:gap-10 sm:py-0 ${
                        contentSizedOnDesktop
                          ? "gap-4"
                          : `min-h-0 flex-1 ${mobileSpotlightActive ? "gap-0 py-0" : "gap-4"}`
                      }`}
                    >
                      <div
                        className={`relative flex w-full items-center max-sm:justify-between px-4 sm:pl-4 sm:pr-6 ${
                          mobileFullScreen
                            ? `max-sm:pt-4 ${headerLeft ? "max-sm:gap-2" : ""} sm:justify-between`
                            : "justify-between"
                        } ${mobileSpotlightActive ? "z-20 shrink-0" : ""} ${headerClassName}`}
                      >
                        {showBack && 
                          <div className="flex items-center justify-center space-x-3">
                            {mobileFullScreen && headerLeft && (
                              <div className="shrink-0">{headerLeft}</div>
                            )}
                            {headerTabs}
                          </div>
                        }
                        
                        {showCancel && 
                          <button
                            type="button"
                            onClick={onClose}
                            className={`shrink-0 rounded-full p-0 custom-transition hover:bg-option-hover`}
                            aria-label="Close"
                          >
                            <CloseIcon />
                          </button>
                        }
                      </div>

                      <div
                        className={
                          mobileFullScreen || mobileSpotlightActive
                            ? `relative flex min-h-0 flex-1 flex-col ${
                                mobileSpotlightActive ? "max-sm:overflow-visible" : ""
                              }`
                            : undefined
                        }
                      >
                        {children}
                      </div>
                    </div>
                  </div>
                </div>

                {guideHint?.position === "right" && (
                  <>
                    <div className="pointer-events-none absolute inset-y-0 left-full z-10 hidden w-[300px] items-center pl-10 lg:flex">
                      <div className="pointer-events-auto w-full">{guideHint.content}</div>
                    </div>
                    {!skipMobileExternalHint && (
                      <div className="pointer-events-auto mt-4 w-full max-w-[300px] lg:hidden">
                        {guideHint.content}
                      </div>
                    )}
                  </>
                )}
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
};

export default CryptoModal;
